import React, { useState } from 'react';
import styled from 'styled-components';
import { PeriodToggle } from '../components/PeriodToggle';
import { PageHeader } from '../components/PageHeader';
import { Button } from '../components/Button';
import { V2ContextBar, ContextToolbar } from '../components/V2ContextBar';
import { Competitor, BrandRow } from './CompetitorsPositioning.types';
import { useCompetitorsPositioning } from 'apiTyped/hooks/v2.competitors.query';
import { useV2Filters } from '../context/V2FiltersContext';

const PERIOD_OPTIONS = [
  { label: '3M', value: '3m' },
  { label: '6M', value: '6m' },
  { label: '12M', value: '12m' }
];

// --- Component ---

export const CompetitorsPositioning: React.FC = () => {
  const { filters } = useV2Filters();
  const [period, setPeriod] = useState('6m');
  const [competitors, setCompetitors] = useState<Competitor[]>([]);

  const {
    isLoading,
    hero,
    competitors: apiCompetitors,
    brandRows,
    rankTiles,
    radarData,
    evolutionData,
    evolutionSeries
  } = useCompetitorsPositioning({
    market: filters.market,
    channel: filters.channel,
    brand: filters.brand,
    period,
    competitors: filters.competitors,
    productType: filters.productType
  });

  // Sync competitors from API on first load
  React.useEffect(() => {
    if (apiCompetitors.length > 0 && competitors.length === 0) {
      setCompetitors(apiCompetitors);
    }
  }, [apiCompetitors]);

  const toggleCompetitor = (name: string) => {
    setCompetitors((prev) =>
      prev.map((c) => (c.name === name ? { ...c, selected: !c.selected } : c))
    );
  };

  if (isLoading || !hero)
    return (
      <Page>
        <PageHeader
          title="Competitors Positioning"
          subtitle="Loading competitive analysis…"
        />
        <LoadingWrap>Loading…</LoadingWrap>
      </Page>
    );

  return (
    <Page>
      {/* Header */}
      <PageHeader
        title="Competitors positioning"
        subtitle="Side-by-side comparison of your brand against selected competitors across ranking, distribution, performance, and trajectory."
      >
        <Button icon={<ExportIcon />}>Export</Button>
      </PageHeader>

      {/* Context bar with competition controls */}
      <V2ContextBar>
        <ContextToolbar>
          <ControlLabel>Competition</ControlLabel>
          {competitors.map((comp) => (
            <CompetitorChip
              key={comp.name}
              $selected={comp.selected}
              onClick={() => toggleCompetitor(comp.name)}
            >
              <Swatch style={{ background: comp.color }} />
              {comp.selected ? comp.name : `+ ${comp.name}`}
              {comp.selected && <X>×</X>}
            </CompetitorChip>
          ))}
          <AddBtn>+ Add competitor</AddBtn>
          <ControlSep />
          <PeriodToggle
            options={PERIOD_OPTIONS}
            value={period}
            onChange={setPeriod}
          />
        </ContextToolbar>
      </V2ContextBar>

      {/* Hero comparison */}
      <HeroComp>
        <HeroGlow />
        <HeroSide>
          <HeroLabel>
            <HeroSwatch style={{ background: hero.yourBrand.color }} />
            Your brand
          </HeroLabel>
          <HeroName>{hero.yourBrand.name}</HeroName>
          <HeroRank>
            Ranked #{hero.yourBrand.rank} of {hero.yourBrand.totalBrands}
          </HeroRank>
          <HeroStats>
            <HeroStat>
              <HeroStatLabel>ND</HeroStatLabel>
              <HeroStatValue $highlight>{hero.yourBrand.nd}</HeroStatValue>
            </HeroStat>
            <HeroStat>
              <HeroStatLabel>Market Share</HeroStatLabel>
              <HeroStatValue $highlight>
                {hero.yourBrand.marketShare}
              </HeroStatValue>
            </HeroStat>
            <HeroStat>
              <HeroStatLabel>POS Net</HeroStatLabel>
              <HeroStatValue $highlight>{hero.yourBrand.posNet}</HeroStatValue>
            </HeroStat>
          </HeroStats>
        </HeroSide>

        <HeroVs>vs.</HeroVs>

        <HeroSide $right>
          <HeroLabel>
            Top competitor
            <HeroSwatch style={{ background: hero.topCompetitor.color }} />
          </HeroLabel>
          <HeroName>{hero.topCompetitor.name}</HeroName>
          <HeroRank>
            Ranked #{hero.topCompetitor.rank} of{' '}
            {hero.topCompetitor.totalBrands}
          </HeroRank>
          <HeroStats $right>
            <HeroStat>
              <HeroStatLabel>ND</HeroStatLabel>
              <HeroStatValue>{hero.topCompetitor.nd}</HeroStatValue>
            </HeroStat>
            <HeroStat>
              <HeroStatLabel>Market Share</HeroStatLabel>
              <HeroStatValue>{hero.topCompetitor.marketShare}</HeroStatValue>
            </HeroStat>
            <HeroStat>
              <HeroStatLabel>POS Net</HeroStatLabel>
              <HeroStatValue>{hero.topCompetitor.posNet}</HeroStatValue>
            </HeroStat>
          </HeroStats>
        </HeroSide>

        <HeroResult>
          <HeroResultLabel>Metrics where you lead</HeroResultLabel>
          <HeroResultBar>
            <HeroBarYou
              style={{
                width: `${
                  (hero.metricsWhere.you / hero.metricsWhere.total) * 100
                }%`
              }}
            />
            <HeroBarThem
              style={{
                width: `${
                  ((hero.metricsWhere.total - hero.metricsWhere.you) /
                    hero.metricsWhere.total) *
                  100
                }%`
              }}
            />
          </HeroResultBar>
          <HeroResultCounts>
            <YouNum>{hero.metricsWhere.you}</YouNum> /{' '}
            <ThemNum>{hero.metricsWhere.total}</ThemNum>
          </HeroResultCounts>
        </HeroResult>
      </HeroComp>

      {/* Radar + Rank */}
      <RadarSplit>
        {/* Performance Radar */}
        <Card>
          <CardHeader>
            <div>
              <CardTitle>Performance radar</CardTitle>
              <CardMeta>7 KPIs normalized 0–100 · 6-month window</CardMeta>
            </div>
            <RadarLegend>
              <span>
                <LegendDot style={{ background: '#c84b1f' }} />
                You
              </span>
              <span>
                <LegendDot style={{ background: '#3a4a6b' }} />
                {hero.topCompetitor.name}
              </span>
            </RadarLegend>
          </CardHeader>
          <RadarSvg viewBox="-160 -160 320 320">
            {/* Grid rings */}
            <g fill="none" stroke="var(--line)" strokeWidth="1">
              {[110, 85, 60, 35].map((r) => (
                <polygon
                  key={r}
                  points={radarData
                    .map((_, i) => {
                      const angle =
                        (Math.PI * 2 * i) / radarData.length - Math.PI / 2;
                      return `${(r * Math.cos(angle)).toFixed(1)},${(
                        r * Math.sin(angle)
                      ).toFixed(1)}`;
                    })
                    .join(' ')}
                />
              ))}
            </g>
            {/* Axes */}
            <g stroke="var(--line-soft)" strokeWidth="1">
              {radarData.map((_, i) => {
                const angle =
                  (Math.PI * 2 * i) / radarData.length - Math.PI / 2;
                return (
                  <line
                    key={i}
                    x1="0"
                    y1="0"
                    x2={110 * Math.cos(angle)}
                    y2={110 * Math.sin(angle)}
                  />
                );
              })}
            </g>
            {/* Labels */}
            <g
              fontFamily="var(--font-sans)"
              fontSize="10"
              fill="var(--ink-muted)"
              fontWeight="500"
            >
              {radarData.map((d, i) => {
                const angle =
                  (Math.PI * 2 * i) / radarData.length - Math.PI / 2;
                const lx = 125 * Math.cos(angle);
                const ly = 125 * Math.sin(angle);
                return (
                  <text
                    key={d.axis}
                    x={lx}
                    y={ly}
                    textAnchor="middle"
                    dominantBaseline="middle"
                  >
                    {d.axis}
                  </text>
                );
              })}
            </g>
            {/* Competitor area */}
            <polygon
              points={radarData
                .map((d, i) => {
                  const angle =
                    (Math.PI * 2 * i) / radarData.length - Math.PI / 2;
                  const r = (d.competitor / 100) * 110;
                  return `${(r * Math.cos(angle)).toFixed(1)},${(
                    r * Math.sin(angle)
                  ).toFixed(1)}`;
                })
                .join(' ')}
              fill="#3a4a6b"
              fillOpacity="0.15"
              stroke="#3a4a6b"
              strokeWidth="2"
              strokeLinejoin="round"
            />
            <g fill="#3a4a6b">
              {radarData.map((d, i) => {
                const angle =
                  (Math.PI * 2 * i) / radarData.length - Math.PI / 2;
                const r = (d.competitor / 100) * 110;
                return (
                  <circle
                    key={i}
                    cx={r * Math.cos(angle)}
                    cy={r * Math.sin(angle)}
                    r="3"
                  />
                );
              })}
            </g>
            {/* Your brand area */}
            <polygon
              points={radarData
                .map((d, i) => {
                  const angle =
                    (Math.PI * 2 * i) / radarData.length - Math.PI / 2;
                  const r = (d.you / 100) * 110;
                  return `${(r * Math.cos(angle)).toFixed(1)},${(
                    r * Math.sin(angle)
                  ).toFixed(1)}`;
                })
                .join(' ')}
              fill="#c84b1f"
              fillOpacity="0.18"
              stroke="#c84b1f"
              strokeWidth="2.5"
              strokeLinejoin="round"
            />
            <g fill="#c84b1f">
              {radarData.map((d, i) => {
                const angle =
                  (Math.PI * 2 * i) / radarData.length - Math.PI / 2;
                const r = (d.you / 100) * 110;
                return (
                  <circle
                    key={i}
                    cx={r * Math.cos(angle)}
                    cy={r * Math.sin(angle)}
                    r="3.5"
                  />
                );
              })}
            </g>
            {/* Scale labels */}
            <g
              fontFamily="var(--font-mono)"
              fontSize="8"
              fill="var(--ink-faint)"
            >
              <text x="3" y="-108">
                100
              </text>
              <text x="3" y="-83">
                75
              </text>
              <text x="3" y="-58">
                50
              </text>
              <text x="3" y="-33">
                25
              </text>
            </g>
          </RadarSvg>
        </Card>

        {/* Where you stand */}
        <Card>
          <CardHeader>
            <div>
              <CardTitle>Where you stand</CardTitle>
              <CardMeta>
                Your rank in each major metric · across 42 tracked brands
              </CardMeta>
            </div>
          </CardHeader>
          <RankGrid>
            {rankTiles.map((tile) => (
              <RankTile key={tile.label} $status={tile.status}>
                <RankTileLabel>{tile.label}</RankTileLabel>
                <RankTileValue>
                  #{tile.rank}
                  <RankUnit>/ {tile.total}</RankUnit>
                </RankTileValue>
                <RankTileVs>
                  {tile.detail} <Delta $down>{tile.delta}</Delta>
                </RankTileVs>
                <StatusBadge $status={tile.status}>
                  {tile.status === 'win' ? 'WIN' : 'BEHIND'}
                </StatusBadge>
              </RankTile>
            ))}
          </RankGrid>
        </Card>
      </RadarSplit>

      {/* Comparison Matrix */}
      <MatrixWrap>
        <MatrixHead>
          <div>
            <CardTitle>Brand-by-brand comparison</CardTitle>
            <CardMeta>All 13 KPIs, sortable · your brand highlighted</CardMeta>
          </div>
          <EvolutionMeta>
            Sort by · <strong>Ranking</strong>
          </EvolutionMeta>
        </MatrixHead>
        <MatrixScroll>
          <Matrix>
            <thead>
              <tr>
                <th>Brand</th>
                <th>
                  Rank<AxisIcon>position</AxisIcon>
                </th>
                <th>
                  # Refs<AxisIcon>unique SKUs</AxisIcon>
                </th>
                <th>
                  ND<AxisIcon>% POS</AxisIcon>
                </th>
                <th>
                  Evol 3M<AxisIcon>vs prior</AxisIcon>
                </th>
                <th>
                  Evol 6M<AxisIcon>vs prior</AxisIcon>
                </th>
                <th>
                  Evol 12M<AxisIcon>vs prior</AxisIcon>
                </th>
                <th>
                  Avg €<AxisIcon>/ btl</AxisIcon>
                </th>
                <th>
                  Visibility<AxisIcon>refs/POS</AxisIcon>
                </th>
                <th>
                  Penetration<AxisIcon>%</AxisIcon>
                </th>
                <th>
                  Loyalty<AxisIcon>%</AxisIcon>
                </th>
                <th>
                  Cons.<AxisIcon>%</AxisIcon>
                </th>
                <th>
                  POS Gain<AxisIcon>period</AxisIcon>
                </th>
                <th>
                  POS Lost<AxisIcon>period</AxisIcon>
                </th>
              </tr>
            </thead>
            <tbody>
              {brandRows.map((row) => (
                <MatrixRow key={row.name + row.rank} $you={row.isYou}>
                  <td>
                    <BrandCell>
                      <BrandSwatch style={{ background: row.color }} />
                      <BrandName>{row.name}</BrandName>
                      {row.isYou && <BrandTag>YOU</BrandTag>}
                      <BrandRank>#{row.rank}</BrandRank>
                    </BrandCell>
                  </td>
                  <CellBest $best={row.isBest?.rank}>{row.rank}</CellBest>
                  <td>{row.refs}</td>
                  <CellBest $best={row.isBest?.nd}>{row.nd}</CellBest>
                  <CellEvolution $value={row.evol3m}>
                    {row.evol3m}
                  </CellEvolution>
                  <CellEvolution $value={row.evol6m}>
                    {row.evol6m}
                  </CellEvolution>
                  <CellEvolution $value={row.evol12m}>
                    {row.evol12m}
                  </CellEvolution>
                  <CellBest $best={row.isBest?.avgPrice}>
                    {row.avgPrice}
                  </CellBest>
                  <CellBest $best={row.isBest?.visibility}>
                    {row.visibility}
                  </CellBest>
                  <CellBest $best={row.isBest?.penetration}>
                    {row.penetration}
                  </CellBest>
                  <CellBest $best={row.isBest?.loyalty}>{row.loyalty}</CellBest>
                  <CellBest $best={row.isBest?.consumption}>
                    {row.consumption}
                  </CellBest>
                  <CellEvolution $value={row.posGain}>
                    {row.posGain}
                  </CellEvolution>
                  <CellEvolution $value={row.posLost}>
                    {row.posLost}
                  </CellEvolution>
                </MatrixRow>
              ))}
            </tbody>
          </Matrix>
        </MatrixScroll>
      </MatrixWrap>

      {/* Evolution Chart */}
      <Card>
        <CardHeader>
          <div>
            <CardTitle>Numeric Distribution — 12 months</CardTitle>
            <CardMeta>Your trajectory against tracked competitors</CardMeta>
          </div>
          <RadarLegend>
            {evolutionSeries.map((s) => (
              <span key={s.key}>
                <LegendDot style={{ background: s.color }} />
                {s.label}
              </span>
            ))}
          </RadarLegend>
        </CardHeader>
        <EvolutionChart viewBox="0 0 720 240" preserveAspectRatio="none">
          {/* Grid */}
          <g stroke="var(--line-soft)" strokeWidth="1">
            <line x1="0" y1="40" x2="720" y2="40" />
            <line x1="0" y1="90" x2="720" y2="90" />
            <line x1="0" y1="140" x2="720" y2="140" />
            <line x1="0" y1="190" x2="720" y2="190" />
          </g>
          <g fontFamily="var(--font-mono)" fontSize="9" fill="var(--ink-faint)">
            <text x="6" y="44">
              60%
            </text>
            <text x="6" y="94">
              45%
            </text>
            <text x="6" y="144">
              30%
            </text>
            <text x="6" y="194">
              15%
            </text>
          </g>
          <g
            fontFamily="var(--font-mono)"
            fontSize="9"
            fill="var(--ink-faint)"
            textAnchor="middle"
          >
            {evolutionData.map((d, i) => (
              <text
                key={d.month}
                x={40 + (i * 660) / (evolutionData.length - 1)}
                y="220"
              >
                {d.month}
              </text>
            ))}
          </g>
          {/* Series lines */}
          {evolutionSeries.map((series) => {
            const pathD = evolutionData
              .map((d, i) => {
                const x = 40 + (i * 660) / (evolutionData.length - 1);
                const y = 210 - ((d.values[series.key] - 10) / 55) * 180;
                return `${i === 0 ? 'M' : 'L'}${x.toFixed(0)},${y.toFixed(0)}`;
              })
              .join(' ');

            const lastPt = evolutionData[evolutionData.length - 1];
            const lastX = 700;
            const lastY = 210 - ((lastPt.values[series.key] - 10) / 55) * 180;

            return (
              <g key={series.key}>
                {series.isYou && (
                  <>
                    <defs>
                      <linearGradient
                        id="youAreaGrad"
                        x1="0"
                        x2="0"
                        y1="0"
                        y2="1"
                      >
                        <stop
                          offset="0%"
                          stopColor={series.color}
                          stopOpacity="0.18"
                        />
                        <stop
                          offset="100%"
                          stopColor={series.color}
                          stopOpacity="0"
                        />
                      </linearGradient>
                    </defs>
                    <path
                      d={`${pathD} L700,210 L40,210 Z`}
                      fill="url(#youAreaGrad)"
                    />
                  </>
                )}
                <path
                  d={pathD}
                  stroke={series.color}
                  strokeWidth={series.isYou ? 2.5 : 2}
                  fill="none"
                  strokeLinecap="round"
                />
                <circle
                  cx={lastX}
                  cy={lastY}
                  r={series.isYou ? 4 : 3}
                  fill={series.color}
                />
              </g>
            );
          })}
        </EvolutionChart>
      </Card>
    </Page>
  );
};

// --- Styled components ---

const Page = styled.div`
  max-width: 1400px;
`;

const LoadingWrap = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 80px 24px;
  color: var(--ink-faint);
  font-size: 14px;
`;

const ExportIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2"
    width="16"
    height="16"
  >
    <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z" />
    <polyline points="14 2 14 8 20 8" />
  </svg>
);

const Controls = styled.section`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 22px;
  padding: 14px 18px;
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: var(--radius-lg);
`;

const ControlLabel = styled.span`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--ink-faint);
  font-weight: 600;
  padding-right: 4px;
`;

const CompetitorChip = styled.span<{ $selected?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 5px 10px 5px 8px;
  border-radius: 100px;
  background: ${({ $selected }) => ($selected ? 'var(--ink)' : 'var(--paper)')};
  border: 1px solid
    ${({ $selected }) => ($selected ? 'var(--ink)' : 'var(--line)')};
  color: ${({ $selected }) => ($selected ? 'var(--white)' : 'var(--ink)')};
  font-size: 12.5px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.15s;
  font-family: var(--font-sans);

  &:hover {
    border-color: var(--ink-faint);
  }
`;

const Swatch = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
`;

const X = styled.span`
  margin-left: 2px;
  opacity: 0.6;
`;

const AddBtn = styled.button`
  padding: 5px 10px;
  border-radius: 100px;
  border: 1px dashed var(--line);
  background: transparent;
  font-size: 12.5px;
  color: var(--ink-muted);
  cursor: pointer;
  font-family: var(--font-sans);

  &:hover {
    border-color: var(--ink-muted);
    color: var(--ink);
  }
`;

const ControlSep = styled.span`
  width: 1px;
  height: 22px;
  background: var(--line);
  margin: 0 4px;
`;

// --- Hero Comparison ---

const HeroComp = styled.section`
  background: linear-gradient(135deg, var(--ink) 0%, #2a2620 100%);
  color: var(--paper);
  border-radius: 14px;
  padding: 28px 32px;
  margin-bottom: 22px;
  position: relative;
  overflow: hidden;
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  gap: 32px;
  align-items: center;
`;

const HeroGlow = styled.div`
  position: absolute;
  top: -100px;
  right: -80px;
  width: 300px;
  height: 300px;
  background: radial-gradient(circle, var(--terracotta) 0%, transparent 70%);
  opacity: 0.25;
  pointer-events: none;
`;

const HeroSide = styled.div<{ $right?: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 6px;
  text-align: ${({ $right }) => ($right ? 'right' : 'left')};
  align-items: ${({ $right }) => ($right ? 'flex-end' : 'flex-start')};
`;

const HeroLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.14em;
  color: rgba(248, 245, 240, 0.55);
  font-weight: 500;
  display: inline-flex;
  align-items: center;
  gap: 6px;
`;

const HeroSwatch = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
`;

const HeroName = styled.div`
  font-family: var(--font-serif);
  font-size: 26px;
  font-weight: 500;
  letter-spacing: -0.02em;
  line-height: 1.1;
`;

const HeroRank = styled.div`
  font-family: var(--font-mono);
  font-size: 13px;
  color: rgba(248, 245, 240, 0.7);
`;

const HeroStats = styled.div<{ $right?: boolean }>`
  display: flex;
  gap: 16px;
  margin-top: 12px;
  justify-content: ${({ $right }) => ($right ? 'flex-end' : 'flex-start')};
`;

const HeroStat = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const HeroStatLabel = styled.div`
  font-size: 10px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: rgba(248, 245, 240, 0.5);
`;

const HeroStatValue = styled.div<{ $highlight?: boolean }>`
  font-family: var(--font-mono);
  font-size: 18px;
  font-weight: 600;
  color: ${({ $highlight }) =>
    $highlight ? 'var(--terracotta-soft)' : 'inherit'};
`;

const HeroVs = styled.div`
  font-family: var(--font-serif);
  font-size: 32px;
  color: rgba(248, 245, 240, 0.4);
  font-style: italic;
  letter-spacing: -0.02em;
`;

const HeroResult = styled.div`
  margin-top: 18px;
  padding-top: 18px;
  border-top: 1px solid rgba(248, 245, 240, 0.1);
  grid-column: 1 / -1;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const HeroResultLabel = styled.div`
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.12em;
  color: rgba(248, 245, 240, 0.55);
`;

const HeroResultBar = styled.div`
  flex: 1;
  margin: 0 20px;
  display: flex;
  height: 8px;
  border-radius: 4px;
  overflow: hidden;
  background: rgba(248, 245, 240, 0.08);
`;

const HeroBarYou = styled.div`
  background: var(--terracotta);
`;

const HeroBarThem = styled.div`
  background: var(--indigo-soft);
  opacity: 0.7;
`;

const HeroResultCounts = styled.div`
  font-family: var(--font-mono);
  font-size: 13px;
  font-weight: 500;
  color: var(--paper);
`;

const YouNum = styled.span`
  color: var(--terracotta-soft);
`;

const ThemNum = styled.span`
  color: rgba(248, 245, 240, 0.55);
`;

// --- Radar + Rank ---

const RadarSplit = styled.section`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 16px;
  margin-bottom: 22px;
`;

const Card = styled.div`
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: 12px;
  padding: 20px 22px;
  margin-bottom: 22px;
`;

const CardHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
`;

const CardTitle = styled.div`
  font-family: var(--font-serif);
  font-size: 17px;
  font-weight: 500;
  letter-spacing: -0.01em;
`;

const CardMeta = styled.div`
  font-size: 11.5px;
  color: var(--ink-faint);
`;

const RadarLegend = styled.div`
  display: flex;
  gap: 14px;
  font-size: 12px;
  color: var(--ink-muted);
  align-items: center;
`;

const LegendDot = styled.span`
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 50%;
  margin-right: 5px;
  vertical-align: middle;
`;

const RadarSvg = styled.svg`
  width: 100%;
  height: 320px;
`;

// --- Rank grid ---

const RankGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 12px;
`;

const RankTile = styled.div<{ $status: 'win' | 'loss' }>`
  background: var(--paper);
  border-radius: 8px;
  padding: 14px 16px;
  position: relative;
`;

const RankTileLabel = styled.div`
  font-size: 10.5px;
  text-transform: uppercase;
  letter-spacing: 0.1em;
  color: var(--ink-faint);
  font-weight: 600;
  margin-bottom: 8px;
`;

const RankTileValue = styled.div`
  font-family: var(--font-serif);
  font-size: 22px;
  font-weight: 500;
  letter-spacing: -0.02em;
  line-height: 1;
  margin-bottom: 4px;
`;

const RankUnit = styled.span`
  font-size: 12px;
  color: var(--ink-faint);
  font-family: var(--font-sans);
  margin-left: 2px;
`;

const RankTileVs = styled.div`
  font-size: 11px;
  color: var(--ink-faint);
  display: flex;
  align-items: center;
  gap: 4px;
`;

const Delta = styled.span<{ $down?: boolean }>`
  font-family: var(--font-mono);
  font-weight: 600;
  color: var(--crimson);
`;

const StatusBadge = styled.span<{ $status: 'win' | 'loss' }>`
  position: absolute;
  top: 12px;
  right: 12px;
  font-size: 9px;
  font-weight: 700;
  letter-spacing: 0.1em;
  color: ${({ $status }) =>
    $status === 'win' ? 'var(--olive)' : 'var(--crimson)'};
  background: ${({ $status }) =>
    $status === 'win' ? 'var(--olive-soft)' : 'var(--crimson-soft)'};
  padding: 2px 6px;
  border-radius: 3px;
`;

// --- Matrix ---

const MatrixWrap = styled.section`
  background: var(--white);
  border: 1px solid var(--line);
  border-radius: 12px;
  overflow: hidden;
  margin-bottom: 22px;
`;

const MatrixHead = styled.div`
  padding: 18px 22px;
  border-bottom: 1px solid var(--line);
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const EvolutionMeta = styled.div`
  display: flex;
  gap: 14px;
  font-size: 12px;
  color: var(--ink-muted);
  align-items: center;

  strong {
    color: var(--ink);
    font-weight: 600;
  }
`;

const MatrixScroll = styled.div`
  overflow-x: auto;
`;

const Matrix = styled.table`
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
  min-width: 1100px;

  thead th {
    background: var(--paper);
    text-align: right;
    padding: 12px 14px;
    font-size: 10.5px;
    text-transform: uppercase;
    letter-spacing: 0.1em;
    color: var(--ink-faint);
    font-weight: 600;
    border-bottom: 1px solid var(--line);
    white-space: nowrap;
    position: sticky;
    top: 0;
  }

  thead th:first-child {
    text-align: left;
    position: sticky;
    left: 0;
    z-index: 2;
    background: var(--paper);
    border-right: 1px solid var(--line);
  }

  tbody td {
    padding: 14px;
    vertical-align: middle;
    text-align: right;
    font-family: var(--font-mono);
    font-feature-settings: 'tnum';
  }

  tbody td:first-child {
    text-align: left;
    font-family: var(--font-sans);
    position: sticky;
    left: 0;
    background: var(--white);
    border-right: 1px solid var(--line-soft);
  }
`;

const AxisIcon = styled.span`
  display: block;
  font-size: 9px;
  color: var(--ink-faint);
  font-weight: 500;
  text-transform: none;
  letter-spacing: 0.02em;
  margin-top: 2px;
`;

const MatrixRow = styled.tr<{ $you?: boolean }>`
  border-bottom: 1px solid var(--line-soft);
  transition: background 0.1s;
  background: ${({ $you }) =>
    $you
      ? 'linear-gradient(90deg, var(--terracotta-soft) 0%, #fef9f5 100%)'
      : 'none'};

  &:last-child {
    border-bottom: 0;
  }

  &:hover {
    background: ${({ $you }) =>
      $you
        ? 'linear-gradient(90deg, var(--terracotta-soft) 0%, var(--paper-warm) 100%)'
        : 'var(--paper-warm)'};
  }

  ${({ $you }) =>
    $you &&
    `
    td:first-child {
      background: linear-gradient(90deg, var(--terracotta-soft) 0%, #fdeee5 100%) !important;
    }
  `}
`;

const BrandCell = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
`;

const BrandSwatch = styled.span`
  width: 10px;
  height: 10px;
  border-radius: 50%;
  flex-shrink: 0;
`;

const BrandName = styled.span`
  font-weight: 600;
`;

const BrandTag = styled.span`
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  color: var(--terracotta);
  background: var(--white);
  padding: 1px 6px;
  border-radius: 3px;
  margin-left: 4px;
`;

const BrandRank = styled.span`
  font-family: var(--font-mono);
  font-size: 11.5px;
  color: var(--ink-faint);
  font-weight: 500;
  margin-left: 4px;
`;

const CellBest = styled.td<{ $best?: boolean }>`
  color: ${({ $best }) => ($best ? 'var(--olive)' : 'inherit')};
  font-weight: ${({ $best }) => ($best ? '600' : 'inherit')};

  ${({ $best }) =>
    $best &&
    `
    &::before {
      content: "▲ ";
      font-size: 9px;
    }
  `}
`;

const CellEvolution = styled.td<{ $value: string }>`
  color: ${({ $value }) => {
    const dir = getEvolDirection($value);
    if (dir === 'up') return 'var(--olive)';
    if (dir === 'down') return 'var(--crimson)';
    return 'var(--ink-faint)';
  }};
`;

// --- Evolution chart ---

const EvolutionChart = styled.svg`
  width: 100%;
  height: 260px;
`;

function getEvolDirection(value: string): 'up' | 'down' | 'flat' {
  if (value.startsWith('+') && value !== '+0') return 'up';
  if (value.startsWith('−') || value.startsWith('-')) return 'down';
  return 'flat';
}
